import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import SiteHeader from "@/components/layout/SiteHeader";
import SiteFooter from "@/components/layout/SiteFooter";
import Home from "@/pages/Home";
import Listings from "@/pages/Listings";
import SpaDetail from "@/pages/SpaDetail";
import SeoLocality from "@/pages/SeoLocality";
import SeoCategory from "@/pages/SeoCategory";
import Plans from "@/pages/Plans";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import OwnerDashboard from "@/pages/OwnerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import NotFound from "@/pages/NotFound";
import { BlogIndex, BlogPostPage } from "@/pages/Blog";
import ScrollToTop from "@/components/layout/ScrollToTop";
import AnalyticsListener from "@/components/layout/AnalyticsListener";

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}

// Catch-all resolver that handles `/spas-in-<locality>` pattern (React Router v7
// does not support inline param prefixes in a single segment).
function CatchAll() {
  const { pathname } = useLocation();
  const m = pathname.match(/^\/spas-in-(.+?)\/?$/);
  if (m) return <SeoLocality slugOverride={m[1]} />;
  return <NotFound />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsListener />
        <Routes>
          <Route path="/" element={<Shell><Home /></Shell>} />
          <Route path="/spas" element={<Shell><Listings /></Shell>} />
          <Route path="/spa/:slug" element={<Shell><SpaDetail /></Shell>} />
          <Route path="/spas-by-category/:category" element={<Shell><SeoCategory /></Shell>} />
          <Route path="/plans" element={<Shell><Plans /></Shell>} />
          <Route path="/about" element={<Shell><About /></Shell>} />
          <Route path="/contact" element={<Shell><Contact /></Shell>} />
          <Route path="/terms" element={<Shell><Terms /></Shell>} />
          <Route path="/terms-and-conditions" element={<Shell><Terms /></Shell>} />
          <Route path="/privacy-policy" element={<Shell><PrivacyPolicy /></Shell>} />
          <Route path="/login" element={<Shell><Login /></Shell>} />
          <Route path="/register" element={<Shell><Register /></Shell>} />
          {/* Blog — public reader. `:slug` catches everything except the
              `category` sub-path which is registered first. */}
          <Route path="/blog" element={<Shell><BlogIndex /></Shell>} />
          <Route path="/blog/category/:category" element={<Shell><BlogIndex /></Shell>} />
          <Route path="/blog/:slug" element={<Shell><BlogPostPage /></Shell>} />
          <Route path="/dashboard/*" element={<Shell><OwnerDashboard /></Shell>} />
          {/* Single hidden admin entry — `/admin` shows login when unauthenticated, dashboard otherwise. */}
          <Route path="/admin/*" element={<Shell><AdminDashboard /></Shell>} />
          <Route path="*" element={<Shell><CatchAll /></Shell>} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
